import React from 'react';
import { FaFilter } from 'react-icons/fa';
import '../css/components/SharedFilter.css';

interface SharedFilterProps {
  // Filter values
  projectId?: string;
  wing?: string;
  unitNumber?: string;
  customerName?: string;
  mobile?: string;
  dateFrom?: string;
  dateTo?: string;
  unitType?: string;
  paymentTerms?: string;
  unitSize?: string;
  
  // Change handlers
  onProjectChange?: (value: string) => void;
  onWingChange?: (value: string) => void;
  onUnitNumberChange?: (value: string) => void;
  onCustomerNameChange?: (value: string) => void;
  onMobileChange?: (value: string) => void;
  onDateFromChange?: (value: string) => void;
  onDateToChange?: (value: string) => void;
  onUnitTypeChange?: (value: string) => void;
  onPaymentTermsChange?: (value: string) => void;
  onUnitSizeChange?: (value: string) => void;
  
  // Actions
  onApply: () => void;
  onClear: () => void;
  
  // Data
  projects?: Array<{ id: number; name: string }>;
  wings?: string[];
  sizes?: string[];
  
  // Visibility control
  show?: boolean;
  onToggle?: () => void;
  
  // Optional labels and placeholders
  dateLabel?: string; // e.g., "Receipt Date" or "Payment Date"
  customerNamePlaceholder?: string; // e.g., "Search by name" or "Customer, unit, booking ID"
  unitNumberPlaceholder?: string; // e.g., "e.g., 301, G-401"
  unitTypePlaceholder?: string; // e.g., "2 BHK, 3 BHK"
}

const SharedFilter: React.FC<SharedFilterProps> = ({
  projectId = '',
  wing = '',
  unitNumber = '',
  customerName = '',
  mobile = '',
  dateFrom = '',
  dateTo = '',
  unitType = '',
  paymentTerms = '',
  unitSize = '',
  onProjectChange,
  onWingChange,
  onUnitNumberChange,
  onCustomerNameChange,
  onMobileChange,
  onDateFromChange,
  onDateToChange,
  onUnitTypeChange,
  onPaymentTermsChange,
  onUnitSizeChange,
  onApply,
  onClear,
  projects = [],
  wings = [],
  sizes = [],
  show = true,
  dateLabel = 'Date',
  customerNamePlaceholder = 'Search by name',
  unitNumberPlaceholder = 'e.g., 301, G-401',
  unitTypePlaceholder = '2 BHK, 3 BHK',
}) => {
  if (!show) return null;

  // Handle Enter key press on any filter input to trigger Apply
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onApply();
    }
  };

  return (
    <div className="shared-filter-container" onKeyDown={handleKeyDown}>
      <div className="shared-filter-content">
          {/* Filter Inputs Row */}
          <div className="filter-inputs-row">
            {onProjectChange && (
              <div className="filter-field">
                <label>Project</label>
                <select 
                  value={projectId} 
                  onChange={(e) => onProjectChange(e.target.value)}
                  className="filter-input"
                >
                  <option value="">All Projects</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {onWingChange && (
              <div className="filter-field">
                <label>Wing</label>
                <select 
                  value={wing} 
                  onChange={(e) => onWingChange(e.target.value)}
                  className="filter-input"
                >
                  <option value="">All</option>
                  {wings.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {onUnitSizeChange && (
              <div className="filter-field">
                <label>Size</label>
                <select 
                  value={unitSize} 
                  onChange={(e) => onUnitSizeChange(e.target.value)}
                  className="filter-input"
                >
                  <option value="">All Sizes</option>
                  {sizes.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {onUnitNumberChange && (
              <div className="filter-field">
                <label>Unit Number</label>
                <input
                  type="text"
                  placeholder={unitNumberPlaceholder}
                  value={unitNumber}
                  onChange={(e) => onUnitNumberChange(e.target.value)}
                  className="filter-input"
                />
              </div>
            )}
            {onUnitTypeChange && (
              <div className="filter-field">
                <label>Unit Type</label>
                <input
                  type="text"
                  placeholder={unitTypePlaceholder}
                  value={unitType}
                  onChange={(e) => onUnitTypeChange(e.target.value)}
                  className="filter-input"
                />
              </div>
            )}
            
            {onCustomerNameChange && (
              <div className="filter-field">
                <label>Customer Name</label>
                <input
                  type="text"
                  placeholder={customerNamePlaceholder}
                  value={customerName}
                  onChange={(e) => onCustomerNameChange(e.target.value)}
                  className="filter-input"
                />
              </div>
            )}
            
            {onMobileChange && (
              <div className="filter-field">
                <label>Mobile Number</label>
                <input
                  type="text"
                  placeholder="Search by mobile"
                  value={mobile}
                  onChange={(e) => onMobileChange(e.target.value)}
                  className="filter-input"
                />
              </div>
            )}
            
            {onDateFromChange && (
              <div className="filter-field">
                <label>{dateLabel} From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => onDateFromChange(e.target.value)}
                  className="filter-input"
                />
              </div>
            )}
            
            {onDateToChange && (
              <div className="filter-field">
                <label>{dateLabel} To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => onDateToChange(e.target.value)}
                  className="filter-input"
                />
              </div>
            )}
            {onPaymentTermsChange && (
              <div className="filter-field">
                <label>Payment Terms</label>
                <select
                  value={paymentTerms}
                  onChange={(e) => onPaymentTermsChange(e.target.value)}
                  className="filter-input"
                >
                  <option value="">All</option>
                  <option value="Down Payment">Down Payment</option>
                  <option value="Loan">Loan</option>
                  <option value="Installments">Installments</option>
                  <option value="Mixed Payment">Mixed Payment</option>
                </select>
              </div>
            )}
          </div>
          
          {/* Action Buttons Row */}
          <div className="filter-actions-row">
            <button className="btn-apply" onClick={onApply}>
              Apply Filters
            </button>
            <button className="btn-clear" onClick={onClear}>
              Clear All
            </button>
          </div>
        </div>
    </div>
  );
};

export default SharedFilter;
